import { useCallback, useEffect, useMemo } from 'react';

import { Tag } from 'primereact/tag';
import { useSearchParams } from 'react-router-dom';

import { useAppSelector } from '@/app/hooks';
import { useGetItRequestQuery } from '@/app/services/issues';
import { ToggleImportantButton } from '@/components/common/toggle-important-button/ToggleImportantButton';
import { ToggleSubscribeButton } from '@/components/common/toggle-subscribe-button/ToggleSubscribeButton';
import {
  RequestEventFragment,
  RequestEventType,
  RequestFragment,
  RequestIssueType,
} from '@/rtk-query.gql';

import { ApproveOrDeny } from './ApproveOrDeny';
import { IT_DEPARTMENT } from './constants';
import { MarkAsComplete } from './MarkAsComplete';
import { MarkAsDuplicate } from './MarkAsDuplicate';
import { MarkAsInProgress } from './MarkAsInProgress';
import { NewComment } from './NewComment';
import { Post } from './Post';
import { StatusUpdate } from './StatusUpdate';
import { useRequestState } from './useRequestState';

import './RequestDetail.css';

export const RequestDetail = () => {
  const { me } = useAppSelector((state) => state.authentication);
  const [params] = useSearchParams();

  const requestId = params.get('requestId');

  const { data: requestData, error } = useGetItRequestQuery({
    id: parseInt(requestId!, 10),
  });

  const request = requestData?.itRequest;
  const requestType = requestData?.itRequest?.type;

  const requestState = useRequestState(request);

  const events = useMemo(() => {
    if (!request) return [];

    return [...request.events, ...request.referencedIn].sort(
      (a, b) =>
        new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
    );
  }, [request]);

  useEffect(() => {
    if (!request || !window.location.hash) return;

    const id = window.location.hash.slice(1);
    const el = document.getElementById(id);
    if (el) el.scrollIntoView();
  }, [request]);

  const canManageRequest = useCallback(
    (request: RequestFragment & { events: RequestEventFragment[] }) => {
      return (
        me.groups.includes(IT_DEPARTMENT) ||
        me.issueManagerTypes.includes(request.type)
      );
    },
    [me.groups, me.issueManagerTypes],
  );

  if (error?.name === 'NOT_FOUND') {
    return (
      <div className="request-container">
        <h1 className="title request-container__title">Not Found</h1>
        <div className="request-container__content">
          The page you're looking for does not exist.
        </div>
      </div>
    );
  }

  if (!request) {
    return <div>Loading...</div>;
  }

  return (
    <div className="request-page">
      <div className="request-container">
        <div className="request-container__title">
          <h1 className="title">{request.title}</h1>
          {requestType === RequestIssueType.IT ? (
            <div className="request-info">
              <Tag
                value={request.issue.department.name}
                severity="success"
                rounded
              />
              <Tag value={request.issue.app.name} severity="success" rounded />
            </div>
          ) : requestType === RequestIssueType.CLIENT ? (
            <div className="request-info">
              <Tag
                value={`${request.issue.account.code}: ${request.issue.account.name}`}
                severity="success"
                rounded
              />
              <Tag
                value={`Implant Date: ${new Date(
                  request.issue.implantedOn,
                ).toLocaleDateString()}`}
                severity="success"
                rounded
              />
              {typeof request.issue.embryosPerFertilization === 'number' ? (
                <Tag
                  value={`# of Embryos/Fertilization: ${request.issue.embryosPerFertilization}`}
                  severity="success"
                  rounded
                />
              ) : null}
            </div>
          ) : null}
        </div>

        <div className="request-container__content">
          <Post
            name={request.requestor?.fullName ?? request.requestorId}
            content={request.content}
            createdAt={request.createdAt}
            updatedAt={request.updatedAt}
          />

          {events.map((event) =>
            event.type === RequestEventType.COMMENT ? (
              <Post
                key={event.id}
                id={event.id.toString()}
                name={event.author?.fullName ?? event.authorAdUserId}
                content={event.content}
                createdAt={event.createdAt}
                updatedAt={event.updatedAt}
              />
            ) : (
              <StatusUpdate
                key={event.id}
                requestId={request.id}
                event={event}
              />
            ),
          )}
        </div>

        <div className="request-container__comment">
          {me && (
            <NewComment request={request} canManageRequest={canManageRequest} />
          )}
        </div>

        <aside className="request-container__aside">
          <ToggleSubscribeButton
            isSubscribed={request.isSubscribed}
            requestId={request.id}
          />
          {me && canManageRequest(request) && (
            <>
              <ToggleImportantButton
                isImportant={request.isImportant}
                requestId={request.id}
              />

              {!requestState ? <ApproveOrDeny request={request} /> : null}

              {request.type !== 'IT' ? (
                requestState === RequestEventType.APPROVED ||
                requestState === RequestEventType.COMPLETED ? (
                  <MarkAsInProgress request={request} />
                ) : null
              ) : !requestState ||
                requestState === RequestEventType.COMPLETED ? (
                <MarkAsInProgress request={request} />
              ) : null}

              {requestState === RequestEventType.IN_PROGRESS ? (
                <MarkAsComplete
                  request={request}
                  canManageRequest={canManageRequest}
                />
              ) : null}
            </>
          )}

          {requestState ? null : (
            <MarkAsDuplicate
              request={request}
              canManageRequest={canManageRequest}
            />
          )}
        </aside>
      </div>
    </div>
  );
};
